import { useState, useMemo } from "react";
import products from "../data/products";

const ITEMS_PER_PAGE = 12;

export function useProducts(initialFilters = {}) {
  const [search, setSearch] = useState(initialFilters.search || "");
  const [categories, setCategories] = useState(initialFilters.categories || []);
  const [priceMin, setPriceMin] = useState(initialFilters.priceMin ?? "");
  const [priceMax, setPriceMax] = useState(initialFilters.priceMax ?? "");
  const [onlySale, setOnlySale] = useState(false);
  const [onlyInStock, setOnlyInStock] = useState(false);
  const [sortBy, setSortBy] = useState("newest");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    let result = [...products];

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        p =>
          p.name.toLowerCase().includes(q) ||
          p.description.toLowerCase().includes(q)
      );
    }

    if (categories.length > 0) {
      result = result.filter(p => categories.includes(p.category));
    }

    if (priceMin !== "") {
      result = result.filter(p => {
        const price = p.salePrice !== null ? p.salePrice : p.price;
        return price >= Number(priceMin);
      });
    }

    if (priceMax !== "") {
      result = result.filter(p => {
        const price = p.salePrice !== null ? p.salePrice : p.price;
        return price <= Number(priceMax);
      });
    }

    if (onlySale) {
      result = result.filter(p => p.salePrice !== null);
    }

    if (onlyInStock) {
      result = result.filter(p => p.stock > 0);
    }

    switch (sortBy) {
      case "price_asc":
        result.sort((a, b) => {
          const pa = a.salePrice !== null ? a.salePrice : a.price;
          const pb = b.salePrice !== null ? b.salePrice : b.price;
          return pa - pb;
        });
        break;
      case "price_desc":
        result.sort((a, b) => {
          const pa = a.salePrice !== null ? a.salePrice : a.price;
          const pb = b.salePrice !== null ? b.salePrice : b.price;
          return pb - pa;
        });
        break;
      case "rating":
        result.sort((a, b) => b.rating - a.rating);
        break;
      default:
        result.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
    }

    return result;
  }, [search, categories, priceMin, priceMax, onlySale, onlyInStock, sortBy]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  function resetPage() {
    setPage(1);
  }

  return {
    products: paginated,
    allFiltered: filtered,
    totalPages,
    page,
    setPage,
    search, setSearch: v => { setSearch(v); resetPage(); },
    categories, setCategories: v => { setCategories(v); resetPage(); },
    priceMin, setPriceMin: v => { setPriceMin(v); resetPage(); },
    priceMax, setPriceMax: v => { setPriceMax(v); resetPage(); },
    onlySale, setOnlySale: v => { setOnlySale(v); resetPage(); },
    onlyInStock, setOnlyInStock: v => { setOnlyInStock(v); resetPage(); },
    sortBy, setSortBy: v => { setSortBy(v); resetPage(); },
  };
}
